// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Pause, 
  Square, 
  SkipForward, 
  RotateCcw, 
  Terminal, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Loader2,
  X,
  ChevronDown
} from 'lucide-react';
import { cn, formatDuration } from '@/lib/utils';
import { useDateFormat } from '@/hooks/useDateFormat';
import { Badge, Button, Select } from '@/components/ui';
import { Modem } from '@/types/firestore';
import { useAuth } from '@/contexts/AuthContext';
import * as runtimeService from '@/services/runtime';
import * as locationService from '@/services/locationService';
import * as esimService from '@/services/esimService';
import { canOrgUseSharedEsims, sharedPoolSentinel } from '@simcheck/shared-types';
import { db } from '@/config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import type { Location, ESimProfile } from '@simcheck/shared-types';
import { HierarchicalLocationSelector } from '../locations/HierarchicalLocationSelector';
import { ExecutionLogViewer, type ExecutionLog } from '../execution/ExecutionLogViewer';

interface ExecutionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  flowId: string;
  nodes: any[];
  edges: any[];
  modems: Modem[];
  onHighlightNode?: (nodeId: string | null) => void;
  isPrimitive?: boolean; // Whether this is a primitive or regular flow
  hasValidationErrors?: boolean; // Whether the flow has validation errors
}

type TabType = 'live' | 'history' | 'debug' | 'logs';

type ExecutionStatus = 'idle' | 'starting' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';

interface NodeResult {
  status: 'pending' | 'running' | 'success' | 'error' | 'skipped';
  startTime?: Date;
  endTime?: Date;
  output?: any;
  error?: string;
}

export function ExecutionPanel({ 
  isOpen, 
  onClose, 
  flowId,
  nodes,
  edges,
  modems,
  onHighlightNode,
  isPrimitive = false,
  hasValidationErrors = false
}: ExecutionPanelProps) {
  const { currentUser, activeOrganization } = useAuth();
  const fmt = useDateFormat();
  // Tabs (live / history / debug) are temporarily hidden — only Logs is
  // surfaced. Tracked for restoration: see GH issue linked in CLAUDE.md.
  const [activeTab, _setActiveTab] = useState<TabType>('logs');
  // Silence unused-warning while the tab bar is hidden.
  void _setActiveTab;
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  // Emulator mode is temporarily disabled — runs always go against real
  // modems. The state is kept (pinned to false) so the rest of the file's
  // wiring continues to compile while the picker is hidden.
  // Restoration tracked: see GH issue linked in CLAUDE.md.
  const useEmulator = false;
  const setUseEmulator = (_v: boolean) => {};
  void setUseEmulator;
  const [executionStatus, setExecutionStatus] = useState<ExecutionStatus>('idle');
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [nodeResults, setNodeResults] = useState<Map<string, NodeResult>>(new Map());
  const [executionHistory, setExecutionHistory] = useState<any[]>([]);
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [breakpoints, setBreakpoints] = useState<Set<string>>(new Set());
  const [startTime, setStartTime] = useState<Date | undefined>();
  const [endTime, setEndTime] = useState<Date | undefined>();
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [selectedESimProfileId, setSelectedESimProfileId] = useState<string>('');
  const [eSimProfiles, setESimProfiles] = useState<ESimProfile[]>([]);
  const [sharedProfiles, setSharedProfiles] = useState<ESimProfile[]>([]);
  const [loadingESimProfiles, setLoadingESimProfiles] = useState(false);

  // Restore latest editor execution on mount
  useEffect(() => {
    if (!flowId || !currentUser || !activeOrganization?.id) return;
    let cancelled = false;
    const organizationId = activeOrganization.id;

    const restore = async () => {
      try {
        const latest = await runtimeService.getLatestEditorExecution(flowId, currentUser.uid, organizationId);
        if (cancelled || !latest) return;

        setCurrentExecutionId(latest.executionId);
        setStartTime(latest.startedAt ? new Date(latest.startedAt) : undefined);

        // Restore logs from the execution
        if (latest.logs && latest.logs.length > 0) {
          setLogs(latest.logs.map((log: any) => ({
            timestamp: log.timestamp ? new Date(log.timestamp) : new Date(),
            level: (log.level === 'warning' ? 'warn' : log.level) as ExecutionLog['level'],
            message: log.message
          })));
        }

        if (latest.status === 'completed') {
          setExecutionStatus('completed');
          setProgress(100);
          setEndTime(latest.completedAt ? new Date(latest.completedAt) : new Date());
        } else if (latest.status === 'failed') {
          setExecutionStatus('failed');
          setEndTime(latest.completedAt ? new Date(latest.completedAt) : new Date());
        } else if (latest.status === 'cancelled') {
          setExecutionStatus('cancelled');
          setEndTime(latest.completedAt ? new Date(latest.completedAt) : new Date());
        } else if (latest.status === 'running' || latest.status === 'pending' || latest.status === 'queued') {
          // Still active — resume polling
          setExecutionStatus('running');
          let lastLogCount = latest.logs?.length || 0;

          runtimeService.pollExecutionStatus(
            latest.executionId,
            (status) => {
              if (cancelled) return;
              if (status.logs && status.logs.length > lastLogCount) {
                const newLogs = status.logs.slice(lastLogCount);
                setLogs(prev => [...prev, ...newLogs.map((log: any) => ({
                  timestamp: log.timestamp ? new Date(log.timestamp) : new Date(),
                  level: (log.level === 'warning' ? 'warn' : log.level) as ExecutionLog['level'],
                  message: log.message
                }))]);
                lastLogCount = status.logs.length;
              }
              if (status.status === 'running') {
                setProgress(50);
              }
            }
          ).then((finalStatus) => {
            if (cancelled) return;
            if (finalStatus.status === 'completed') {
              setExecutionStatus('completed');
              setProgress(100);
            } else if (finalStatus.status === 'cancelled') {
              setExecutionStatus('cancelled');
            } else {
              setExecutionStatus('failed');
            }
            setEndTime(finalStatus.completedAt ? new Date(finalStatus.completedAt) : new Date());
          }).catch((err) => {
            if (cancelled) return;
            console.error('Failed to resume polling:', err);
          });
        }
      } catch (err) {
        console.error('Failed to restore editor execution:', err);
      }
    };

    restore();
    return () => { cancelled = true; };
  }, [flowId, currentUser, activeOrganization?.id]);

  // Load available eSIM profiles on mount
  useEffect(() => {
    if (activeOrganization?.id) {
      loadAvailableESimProfiles();
    }
  }, [activeOrganization?.id]);

  const loadAvailableESimProfiles = async () => {
    if (!activeOrganization) return;
    setLoadingESimProfiles(true);
    try {
      const profiles = await esimService.getProfilesForOrg(activeOrganization.id);
      setESimProfiles(profiles.filter(p => p.status === 'available'));
      // Shared inventory (super-admin-owned resident SIMs) for granted orgs.
      if (canOrgUseSharedEsims(activeOrganization as any)) {
        const shared = await esimService.getSharedProfiles().catch(() => []);
        setSharedProfiles(shared.filter(p => p.status === 'available'));
      } else {
        setSharedProfiles([]);
      }
    } catch (error) {
      console.error('Failed to load eSIM profiles:', error);
    } finally {
      setLoadingESimProfiles(false);
    }
  };

  // Execute flow at selected location using real runtime
  const executeFlow = async () => {
    if (!currentUser || !activeOrganization) {
      addLog('User not authenticated', 'error');
      return;
    }

    // Using hardcoded device/modem for now, so location is optional
    addLog('Starting flow execution...', 'info');
    addLog('Using hardcoded device/modem (edge_b827ebc26531 / 863547050006168)', 'info');
    addLog(`Mode: ${useEmulator ? 'Emulator (Mock Modem)' : 'Real-Time (Live Modem)'}`, 'info');
    
    if (selectedLocationId) {
      const location = locations.find(l => l.id === selectedLocationId);
      if (location) {
        addLog(`Location: ${location.name}`, 'info');
        if (location.modems.length > 0) {
          addLog(`Available modems: ${location.modems.length}`, 'info');
        }
      }
    }
    setExecutionStatus('starting');
    setStartTime(new Date());
    setEndTime(undefined);
    setNodeResults(new Map());
    setProgress(0);

    try {
      // Load the flow definition from Firestore (primitives or flows collection)
      const collection = isPrimitive ? 'primitives' : 'flows';
      addLog(`Loading ${isPrimitive ? 'primitive' : 'flow'} definition...`, 'info');
      const flowDoc = await getDoc(doc(db, collection, flowId));
      if (!flowDoc.exists()) {
        throw new Error(`${isPrimitive ? 'Primitive' : 'Flow'} not found`);
      }
      const flowData = flowDoc.data();
      
      // Transform to match FlowDefinition structure with nested flow property
      // Primitives store flow in data.flow, regular flows store nodes/edges at top level
      const flowDefinition = {
        ...flowData,
        id: flowId,
        flow: flowData.flow || {
          nodes: flowData.nodes || [],
          edges: flowData.edges || [],
          entryPoint: (flowData.nodes || flowData.flow?.nodes || []).find((n: any) => n.type === 'start')?.id || '',
          exitPoints: (flowData.nodes || flowData.flow?.nodes || []).filter((n: any) => n.type === 'end').map((n: any) => n.id) || []
        }
      };
      addLog(`${isPrimitive ? 'Primitive' : 'Flow'} definition loaded`, 'info');

      // Call the real runtime with the flow definition
      addLog(`Executing at location: ${location?.name}`, 'info');
      addLog('Calling runtime execution API...', 'info');
      
      // For now, use hardcoded device/modem (same as E2E tests)
      // TODO: Add device/modem selector UI
      const executionContext = {
        deviceId: 'edge_b827ebc26531',
        modemImei: '863547050006168',
        eSimProfileId: selectedESimProfileId || undefined
      };
      addLog(`Using device: ${executionContext.deviceId}, modem: ${executionContext.modemImei}`, 'info');
      if (selectedESimProfileId) {
        const profile = eSimProfiles.find(p => p.id === selectedESimProfileId);
        if (profile) {
          addLog(`eSIM Profile: ${profile.name} (${profile.networkName || 'Unknown Network'})`, 'info');
        }
      }
      
      // Clear previous execution state before starting new one
      setLogs([]);

      const response = await runtimeService.executeFlow(
        flowDefinition,
        {}, // inputs
        currentUser.uid,
        activeOrganization.id,
        executionContext,
        selectedLocationId || undefined, // Optional location
        useEmulator, // Pass emulator mode flag
        undefined, // testRunId
        'editor' // source — tags this execution for restoration
      );
      
      setCurrentExecutionId(response.executionId);
      addLog(`Execution started: ${response.executionId}`, 'info');
      setExecutionStatus('running');

      // Track how many logs we've already displayed to avoid duplicates
      let lastLogCount = 0;

      // Poll for execution status
      const finalStatus = await runtimeService.pollExecutionStatus(
        response.executionId,
        (status) => {
          // Only add new logs (avoid duplicates from repeated polling)
          if (status.logs && status.logs.length > lastLogCount) {
            // Add only the new logs since last poll (preserve structured format)
            const newLogs = status.logs.slice(lastLogCount);
            setLogs(prev => [...prev, ...newLogs.map(log => ({
              timestamp: log.timestamp ? new Date(log.timestamp) : new Date(),
              level: (log.level === 'warning' ? 'warn' : log.level) as ExecutionLog['level'],
              message: log.message
            }))]);
            lastLogCount = status.logs.length;
          }

          // Update progress (estimate based on status)
          if (status.status === 'running') {
            setProgress(50);
          }
        }
      );

      // Execution complete
      if (finalStatus.status === 'completed') {
        setExecutionStatus('completed');
        setProgress(100);
        addLog('Flow execution completed successfully', 'info');
        
        // Mark all nodes as success (in real implementation, track individual nodes)
        const results = new Map<string, NodeResult>();
        nodes.forEach(node => {
          results.set(node.id, {
            status: 'success',
            startTime: new Date(finalStatus.startedAt),
            endTime: new Date(finalStatus.completedAt),
            output: finalStatus.result
          });
        });
        setNodeResults(results);
      } else {
        setExecutionStatus('failed');
        addLog(`Flow execution failed: ${finalStatus.result?.error || 'Unknown error'}`, 'error');
        
        // Mark nodes as error
        const results = new Map<string, NodeResult>();
        nodes.forEach(node => {
          results.set(node.id, {
            status: 'error',
            startTime: new Date(finalStatus.startedAt),
            endTime: new Date(finalStatus.completedAt),
            error: finalStatus.result?.error || 'Execution failed'
          });
        });
        setNodeResults(results);
      }
      
      setEndTime(new Date());
      
    } catch (error: any) {
      console.error('Flow execution error:', error);
      setExecutionStatus('failed');
      addLog(error.message, 'error');
      setEndTime(new Date());
    }
  };

  const simulateExecution = async () => {
    const results = new Map<string, NodeResult>();
    
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      setCurrentNodeId(node.id);
      onHighlightNode?.(node.id);
      
      addLog(`Executing node: ${node.data?.label || node.type}`, 'info');
      
      results.set(node.id, {
        status: 'running',
        startTime: new Date()
      });
      setNodeResults(new Map(results));
      
      // Simulate execution time
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
      
      // Check for breakpoint
      if (breakpoints.has(node.id)) {
        setExecutionStatus('paused');
        addLog(`Breakpoint hit at node: ${node.data?.label || node.type}`, 'debug');
        return;
      }
      
      // Random success/failure
      const success = Math.random() > 0.1;
      
      results.set(node.id, {
        status: success ? 'success' : 'error',
        startTime: results.get(node.id)?.startTime,
        endTime: new Date(),
        output: success ? { result: 'OK' } : undefined,
        error: success ? undefined : 'Command failed'
      });
      setNodeResults(new Map(results));
      setProgress(Math.round(((i + 1) / nodes.length) * 100));
      
      if (!success) {
        addLog(`Node failed: ${node.data?.label || node.type}`, 'error');
        setExecutionStatus('failed');
        setEndTime(new Date());
        setCurrentNodeId(null);
        onHighlightNode?.(null);
        return;
      }
      
      addLog(`Node completed: ${node.data?.label || node.type}`, 'info');
    }
    
    // All nodes completed
    setExecutionStatus('completed');
    setEndTime(new Date());
    setCurrentNodeId(null);
    onHighlightNode?.(null);
    setProgress(100);
    addLog('Flow execution completed successfully', 'info');
  };

  const stopExecution = async () => {
    if (currentExecutionId) {
      try {
        await runtimeService.cancelExecution(currentExecutionId);
        addLog('Cancellation requested', 'info');
      } catch (error: any) {
        addLog(`Failed to cancel: ${error.message}`, 'error');
      }
    }

    setExecutionStatus('cancelled');
    setEndTime(new Date());
    setCurrentNodeId(null);
    onHighlightNode?.(null);
    addLog('Execution stopped by user', 'info');
  };

  const pauseExecution = () => {
    setExecutionStatus('paused');
    addLog('Execution paused', 'info');
  };

  const resumeExecution = () => {
    setExecutionStatus('running');
    addLog('Execution resumed', 'info');
    simulateExecution();
  };

  const stepExecution = () => {
    addLog('Step execution (not implemented yet)', 'debug');
  };

  const addLog = (message: string, level: ExecutionLog['level'] = 'info') => {
    setLogs(prev => [...prev, {
      timestamp: new Date(),
      level,
      message
    }]);
  };

  const toggleBreakpoint = (nodeId: string) => {
    const newBreakpoints = new Set(breakpoints);
    if (newBreakpoints.has(nodeId)) {
      newBreakpoints.delete(nodeId);
    } else {
      newBreakpoints.add(nodeId);
    }
    setBreakpoints(newBreakpoints);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Loader2 className="w-4 h-4 animate-spin text-accent-blue" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-accent-green" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-accent-red" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-muted-foreground/70" />;
      case 'skipped':
        return <AlertCircle className="w-4 h-4 text-accent-orange" />;
      default:
        return null;
    }
  };

  const getExecutionControls = () => {
    switch (executionStatus) {
      case 'idle':
      case 'completed':
      case 'failed':
      case 'cancelled':
        return (
          <Button
            variant="default"
            size="sm"
            icon={executionStatus === 'idle' ? <Play className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
            onClick={executeFlow}
            disabled={hasValidationErrors}
            title={hasValidationErrors ? 'Fix validation errors before executing' : undefined}
          >
            {executionStatus === 'idle' ? 'Execute' : 'Re-run'}
          </Button>
        );
      
      case 'running':
        return (
          <>
            <Button
              variant="ghost"
              size="sm"
              icon={<Pause className="w-4 h-4" />}
              onClick={pauseExecution}
            >
              Pause
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<Square className="w-4 h-4 text-accent-red" />}
              onClick={stopExecution}
            >
              Stop
            </Button>
          </>
        );
      
      case 'paused':
        return (
          <>
            <Button
              variant="default"
              size="sm"
              icon={<Play className="w-4 h-4" />}
              onClick={resumeExecution}
            >
              Resume
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<SkipForward className="w-4 h-4" />}
              onClick={stepExecution}
            >
              Step
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<Square className="w-4 h-4 text-accent-red" />}
              onClick={stopExecution}
            >
              Stop
            </Button>
          </>
        );
      
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-card border-b border-border dark:border-border-subtle">
        <div className="flex items-center gap-4 flex-1">
          <h3 className="text-sm font-semibold text-foreground">
            Execution Panel
          </h3>
          
          {/* Location Selector */}
          {executionStatus === 'idle' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Location:
                </span>
                <div className="w-80">
                  <HierarchicalLocationSelector
                    selectedLocationId={selectedLocationId || null}
                    onSelectLocation={(id) => setSelectedLocationId(id || '')}
                    placeholder="Search locations..."
                  />
                </div>
              </div>

              {/* eSIM Profile Selector */}
              <div className="flex items-center gap-2 border-l border-border dark:border-border-subtle pl-4">
                <span className="text-xs text-muted-foreground">
                  eSIM Profile (Optional):
                </span>
                <select
                  value={selectedESimProfileId}
                  onChange={(e) => setSelectedESimProfileId(e.target.value)}
                  disabled={loadingESimProfiles}
                  className="px-2 py-1 text-xs bg-card border border-border dark:border-border-subtle rounded focus:outline-none focus:ring-2 focus:ring-accent-blue text-foreground disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
                >
                  <option value="">None</option>
                  {eSimProfiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name} - {profile.networkName || 'Unknown Network'}
                    </option>
                  ))}
                  {esimService.sharedGroupsForOrg(sharedProfiles, activeOrganization).map((g) => (
                    <option key={g.groupId} value={sharedPoolSentinel(g.groupId)}>
                      SimCheck Shared — {g.groupName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mode picker hidden — runs always go against real modems.
                  Restoration tracked: see GH issue linked in CLAUDE.md. */}
            </div>
          )}
          
          {/* Status indicator */}
          {executionStatus !== 'idle' && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Status:</span>
                <Badge
                  variant={
                    executionStatus === 'running' ? 'info' :
                    executionStatus === 'completed' ? 'success' :
                    executionStatus === 'failed' ? 'error' :
                    'secondary'
                  }
                >
                  {executionStatus.charAt(0).toUpperCase() + executionStatus.slice(1)}
                </Badge>
                {startTime && (
                  <span className="text-muted-foreground/70">
                    ({formatDuration(startTime.getTime(), endTime?.getTime())})
                  </span>
                )}
              </div>
              
              {/* Progress bar */}
              {executionStatus === 'running' && (
                <div className="w-32 h-2 bg-card rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-accent-blue transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Execution controls */}
          {getExecutionControls()}
          
          {/* Close button */}
          <Button
            variant="ghost"
            size="sm"
            icon={<X className="w-4 h-4" />}
            onClick={onClose}
          />
        </div>
      </div>
      
      {/* Tab bar hidden — Live / History / Debug temporarily removed to ship
          the editor redesign. Restoration tracked in CLAUDE.md / GH issue. */}
      
      {/* Content */}
      <div className="overflow-auto" style={{ height: 'calc(100% - 96px)' }}>
        {/* Live View Tab */}
        {activeTab === 'live' && (
          <div className="p-4">
            {executionStatus === 'idle' ? (
              <div className="text-center py-8 text-muted-foreground/70">
                <Play className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-xs">Select a location and click "Execute" to start flow execution</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Node execution list */}
                {nodes.map(node => {
                  const result = nodeResults.get(node.id);
                  const isCurrent = currentNodeId === node.id;
                  
                  return (
                    <div
                      key={node.id}
                      className={cn(
                        'flex items-center gap-2 p-2 rounded border transition-all cursor-pointer',
                        isCurrent ? 'bg-accent-blue/10 border-accent-blue' :
                        result?.status === 'success' ? 'bg-accent-green/10 border-accent-green' :
                        result?.status === 'error' ? 'bg-accent-red/10 border-accent-red' :
                        'bg-card border-border dark:border-border-subtle'
                      )}
                      onClick={() => onHighlightNode?.(node.id)}
                    >
                      {/* Status icon */}
                      <div className="flex-shrink-0">
                        {isCurrent ? <Loader2 className="w-4 h-4 animate-spin text-accent-blue" /> :
                         result ? getStatusIcon(result.status) :
                         <div className="w-4 h-4 rounded-full border-2 border-border dark:border-border-subtle" />}
                      </div>
                      
                      {/* Node info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-xs text-foreground">
                            {node.data?.label || node.type}
                          </span>
                          <span className="text-[10px] text-muted-foreground/70">
                            ({node.id.substring(0, 8)})
                          </span>
                        </div>
                        
                        {/* Result output */}
                        {result?.output && (
                          <div className="mt-0.5 text-[10px] text-muted-foreground font-mono truncate">
                            Output: {JSON.stringify(result.output).substring(0, 100)}
                          </div>
                        )}
                        
                        {/* Error message */}
                        {result?.error && (
                          <div className="mt-0.5 text-[10px] text-accent-red">
                            Error: {result.error}
                          </div>
                        )}
                      </div>
                      
                      {/* Timing */}
                      {result?.startTime && (
                        <div className="text-[10px] text-muted-foreground/70">
                          {formatDuration(result.startTime.getTime(), result.endTime?.getTime())}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="p-4">
            {executionHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground/70">
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-xs">No execution history yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {executionHistory.map((execution, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-2 p-2 bg-card rounded border border-border dark:border-border-subtle hover:border-accent-blue transition-colors cursor-pointer"
                  >
                    {getStatusIcon(execution.status)}
                    <div className="flex-1">
                      <div className="text-xs font-medium text-foreground">
                        Execution #{execution.id}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {fmt.formatDateTime(execution.startTime)}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground/70">
                      {formatDuration(execution.startTime, execution.endTime)}
                    </div>
                    <Button variant="ghost" size="sm">
                      View Details
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Debug Tab */}
        {activeTab === 'debug' && (
          <div className="p-3">
            <div className="mb-3">
              <h4 className="text-xs font-semibold text-foreground mb-2">
                Breakpoints
              </h4>
              <div className="space-y-0.5">
                {nodes.map(node => (
                  <label 
                    key={node.id} 
                    className="flex items-center gap-2 p-1.5 hover:bg-muted rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={breakpoints.has(node.id)}
                      onChange={() => toggleBreakpoint(node.id)}
                      className="rounded text-accent-blue border-border dark:border-border-subtle"
                    />
                    <span className="text-xs text-foreground">
                      {node.data?.label || node.type} ({node.id.substring(0, 8)})
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {currentNodeId && (
              <div className="mt-3">
                <h4 className="text-xs font-semibold text-foreground mb-2">
                  Current Variables
                </h4>
                <div className="bg-muted p-2 rounded font-mono text-[10px] text-accent-green overflow-x-auto">
                  <pre>{JSON.stringify(nodeResults.get(currentNodeId)?.output || {}, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="p-2 h-full">
            <ExecutionLogViewer
              logs={logs}
              isLive={executionStatus === 'running'}
              autoScroll={true}
              showFilters={true}
              showExport={true}
              maxHeight="calc(100% - 40px)"
            />
          </div>
        )}
      </div>
    </div>
  );
}

